import { FormApi } from "final-form";
import _ from "lodash";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { useDispatch } from "src/store";
import { Modal } from "src/ui/shared/Modal/Modal";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { useAllCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { FileContentType } from "@dashboard/files/types";
import { createSource } from "@dashboard/products/components/ProductBuilder/components/Recipes";
import { updateProduct } from "@dashboard/products/store";
import { AndroidModule, Product } from "@dashboard/products/types";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { Tooltip } from "@shared/Tooltip";

import SharedUtils from "./ModulePanel.utils";

const schema = z.object({
    fileId: z.string({ message: i18n.t("common:validation.fieldRequired") }),
});

const useStyles = makeStyles()((theme) => ({
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(2), marginTop: theme.spacing(3) },
}));

type FormValues = {
    fileId: string;
};

type Props = {
    product: Product | null;
    open: boolean;
    onToggle: (value: boolean) => void;
};

const contentTypes = [
    FileContentType.ZipCompressedV2,
    FileContentType.Zip,
    FileContentType.ZipMultipart,
    FileContentType.ZipCompressed,
];

export const AddBootAnimationDialog = (props: Props) => {
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "modals", "common", "sidepanels"]);
    const validate = useValidationSchema(schema);

    const modules = props.product?.customBuildConfig?.modules || [];
    const sources = props.product?.customBuildConfig?.sources || {};

    const { data: workspace } = useCurrentWorkspace();
    const { data } = useAllCurrentWorkspaceFiles({ contentTypes }, { skip: !props.open });

    const accessibleFiles = data.filter((item) => !SharedUtils.checkFileSourceExists(modules, sources, item.id));
    const fileOptions = _.orderBy(
        accessibleFiles.map((item) => ({ label: item.filename, value: item.id })),
        "label",
    );

    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const formDisabled = !fileOptions.length || cannotManageProducts || !props.product;

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const file = data.find((item) => item.id === values.fileId);
        if (file && props.product) {
            const [sourceId, newSource] = createSource(file, sources);
            const newModule: AndroidModule = {
                type: "prebuilt_bootanimation",
                name: "boot_animation",
                sourceId,
                tags: ["Own"],
            };

            const newProduct = { ...props.product };
            if (newProduct.customBuildConfig) {
                newProduct.customBuildConfig = { ...newProduct.customBuildConfig };
                newProduct.customBuildConfig.modules = [...newProduct.customBuildConfig.modules, newModule];
                newProduct.customBuildConfig.sources = { ...newProduct.customBuildConfig.sources, ...newSource };
            }
            await dispatch(updateProduct(newProduct));
        }
        form.restart();
        props.onToggle(false);
    };
    const getTooltipTitle = () => {
        if (!fileOptions.length) {
            return t("modals:manageProductModules.addBootAnimation.noFilesAvailable");
        }
        if (cannotManageProducts) {
            return t("common:tooltip.cannotManageProducts");
        }
        if (!props.product) {
            return t("common:tooltip.noProductSelected");
        }
        return undefined;
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{
                fileId: fileOptions[0]?.value,
            }}
            render={({ handleSubmit, form, submitting }) => (
                <Modal
                    open={props.open}
                    onClose={() => props.onToggle(false)}
                    title={t("modals:manageProductModules.addBootAnimation.title")}
                    endButton={
                        <Tooltip hide={!formDisabled} title={getTooltipTitle()}>
                            <PrimaryButton onClick={form.submit} disabled={submitting || formDisabled} loading={submitting}>
                                {t("common:button.add")}
                            </PrimaryButton>
                        </Tooltip>
                    }
                    description={t("modals:manageProductModules.addBootAnimation.description")}
                >
                    <form onSubmit={handleSubmit} className={classes.form}>
                        <ControlledSelectField
                            name="fileId"
                            options={fileOptions}
                            disabled={formDisabled}
                            label={
                                fileOptions.length
                                    ? t("common:label.file")
                                    : t("modals:manageProductModules.addBootAnimation.noFilesAvailable")
                            }
                        />
                    </form>
                </Modal>
            )}
        />
    );
};
